import React from 'react';
import { getPlaybookById } from '../../../data/playbookTaxonomy';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { 
  ChartBarIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  DocumentMagnifyingGlassIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';

const StorytellingWithDataStage1 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 1: Link to Business Outcome + Analyse Data"
        icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />}
        heading="Data Analysis and Business Alignment"
      >
        <ExpandableCallout
          title="What do I need to DO in this stage?"
          type="info"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 4 things:</p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                Identify the problem
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                Source the most impactful supporting data set(s) you can find
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3.</span>
                Analyse, extract insights and document
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">4.</span>
                Link to Business Goals
              </li>
            </ol>
          </div>
        </ExpandableCallout>

        <PlaybookSection
          title="Identify the Problem"
          icon={<LightBulbIcon className="h-7 w-7 text-primary-light" />}
          heading="Problem Definition"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Begin by clearly defining the issue you've observed or hypothesised. This could be based on your instincts, one-on-one conversations, or your own observations. Having a strong sense of what needs to be done is essential, but <strong>crucially</strong> you'll need to back it up with data to tell a compelling story and influence others effectively.
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <div className="border-l-4 border-primary-light pl-4">
              <p className="text-gray-300 italic">
                Example problem statement:
              </p>
              <p className="text-gray-300 mt-2">
                20% increase in turnover among our high-performing teams. This trend is disrupting project continuity and team stability, leading to delays and increased recruitment costs. Preliminary feedback from exit interviews indicates potential issues with career development opportunities and team dynamics.
              </p>
            </div>
          </PlaybookCard>
        </PlaybookSection>

        <PlaybookSection
          title="Source the Most Impactful Supporting Data"
          icon={<DocumentMagnifyingGlassIcon className="h-7 w-7 text-primary-light" />}
          heading="Data Collection"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 font-semibold mb-4">What data is most relevant to the problem?</p>
            <p className="text-gray-300 mb-4">
              Identify people and talent data that directly radiates insights on the problem.
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-semibold mb-2">Example sources:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    Team health check
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    Engagement survey/pulse
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    Manager 1-2-1s
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    HRIS/ATS data
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">Example external sources:</h3>
                <p className="text-gray-300 mb-4">
                  External benchmarks provide extra clout to your pov. Here's a couple of useful tools that provide this:
                </p>
                <ol className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light font-bold mr-2">1.</span>
                    Compensation benchmarking ⇒ figures.hr
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light font-bold mr-2">2.</span>
                    People and talent metric benchmarking ⇒ peoplemetrics.fyi
                  </li>
                </ol>
              </div>
            </div>
          </PlaybookCard>

          <ToolCallout
            tool="peoplemetrics"
            label="People Metrics"
            href="http://peoplemetrics.fyi"
            description="We built this tool to provide startup specific benchmarks for 19 x People & Talent metrics. You can use it for free for 14 days"
          />

          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/data-storytelling/my-peoplemetrics-chart_(50).png"
            alt="People Metrics Chart Example"
            caption="Example of a people metrics chart showing key HR metrics over time"
            className="mt-6 rounded-xl overflow-hidden"
          />
        </PlaybookSection>

        <PlaybookSection
          title="Analyse and Look for Patterns"
          icon={<PresentationChartLineIcon className="h-7 w-7 text-primary-light" />}
          heading="Data Analysis"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Ok so you've identified the most impactful people and talent data sources. Now it's time to dig a little deeper.
            </p>
          </div>

          <ExpandableCallout
            title="Example Analysis"
            type="info"
            defaultOpen={true}
          >
            <p className="text-gray-300">
              Let's use a team health check as an example. It's a fun one because it's a mix of quant and qualitative.
            </p>
          </ExpandableCallout>

          <div className="space-y-6 mt-6">
            <div>
              <h3 className="text-white font-semibold mb-4">Quantitative Example:</h3>
              <p className="text-gray-300 mb-4">
                We ran a team health check across 240 people (18 teams)
              </p>
              <p className="text-gray-300">
                92% of teams rated 'Inter-team alignment' as 'A dumpster fire'
              </p>
            </div>

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/data-storytelling/team-health-check-report.png"
              alt="Team Health Check Report"
              caption="Sample team health check report showing team performance metrics"
              className="mt-6 rounded-xl overflow-hidden"
            />

            <div>
              <h3 className="text-white font-semibold mb-4">Qualitative Example:</h3>
              <p className="text-gray-300 mb-4">
                6/22 teams (28%) have identified theme #1 as a top problem that impacts #team_health and by extension productivity and execution speed
              </p>
            </div>

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/data-storytelling/team-health-miro-themes.png"
              alt="Team Health Miro Themes"
              caption="Miro board showing team health themes and patterns"
              className="mt-6 rounded-xl overflow-hidden"
            />

            <ExpandableCallout
              title="Key Insights"
              type="info"
              defaultOpen={true}
            >
              <p className="text-gray-300">
                From this quick example we have both qualitative and quantitative data that points at the following 2 opportunities to improve team health and reduce the risk of our example retention problem:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  Lack of aligned objectives and ways of working
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  Lack of manager support
                </li>
              </ul>
            </ExpandableCallout>
          </div>
        </PlaybookSection>

        <PlaybookSection
          title="Link to a Strategic Business Goal"
          icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
          heading="Business Alignment"
        >
          <ExpandableCallout
            title="Business Outcome Connection"
            type="info"
            defaultOpen={true}
          >
            <p className="text-gray-300">
              If you want to be heard, the story you are going to tell and the data that will support it, needs to be grounded in a <strong>business outcome.</strong>
            </p>
          </ExpandableCallout>

          <div className="prose prose-invert max-w-none mt-6">
            <p className="text-gray-300">
              Ok so you've got a validated problem. Now we need to link it to a strategic business goal, otherwise it's just 'another problem'
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <h3 className="text-white font-semibold mb-4">Action:</h3>
            <p className="text-gray-300 mb-4">
              Begin by conducting a thorough review of your business's strategy. This might involve revisiting the company's long-term goals, last board pack, strategic priorities, and key performance indicators (KPIs).
            </p>
            <p className="text-gray-300 mb-4">
              If you don't know–engage with other executives and the founder or CEO to gain insights into which business outcomes are currently prioritised.
            </p>
            <p className="text-gray-300 mb-4">
              Ask yourself the following questions to guide your review:
            </p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                What are the primary goals that the company aims to achieve in the short and long term?
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                <div>
                  Which 'commercial needles' are most critical right now? These might include:
                  <ul className="mt-2 space-y-2 ml-8">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Revenue growth
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Market expansion
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Customer acquisition
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Cost reduction
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Capital efficiency
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      Scalability
                    </li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3.</span>
                How do current projects and initiatives align with these goals? Understanding this can help you identify where your work or the work of your team fits into the larger picture.
              </li>
            </ol>
          </PlaybookCard>

          <ExpandableCallout
            title="Scenario Example"
            type="info"
            defaultOpen={true}
            className="mt-6"
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Let's assume the company's focus is on revenue growth and customer acquisition.
              </p>
              <h4 className="text-white font-semibold">Summary:</h4>
              <p className="text-gray-300">
                To support these strategic priorities, we recognise that retaining 20% more employees directly impacts our hiring and onboarding processes. Specifically, a 20% increase in retention leads to an additional 20% in new hires beyond the planned hiring numbers. These new hires will need onboarding and training, with an average ramp-up time of 2.5 months.
              </p>
              <h4 className="text-white font-semibold">Detailed Breakdown:</h4>
              <ol className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">1.</span>
                  <div>
                    <strong>Revenue Growth:</strong>
                    <ul className="mt-2 space-y-2">
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        By improving retention, we reduce turnover costs and maintain productivity levels, contributing to steady revenue growth.
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        Each retained employee adds more value over time, as their ramp-up period has already been completed.
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">2.</span>
                  <div>
                    <strong>Customer Acquisition:</strong>
                    <ul className="mt-2 space-y-2">
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        A stable and experienced team improves customer service and satisfaction, leading to higher customer retention and potential new customer referrals.
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        Well-trained employees can better handle new customer onboarding, improving the overall acquisition process.
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
              <h4 className="text-white font-semibold">Impact on People Team and Operations:</h4>
              <ul className="space-y-2 mt-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2 mt-1">—</span>
                  <div className="flex flex-col sm:flex-row w-full">
                    <span className="font-semibold text-white min-w-[140px] sm:min-w-[180px]">
                      People Team:
                    </span>
                    <span className="text-gray-300">
                      Will need to adjust the hiring plan to accommodate the increased retention and manage the additional onboarding and training processes.
                    </span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2 mt-1">—</span>
                  <div className="flex flex-col sm:flex-row w-full">
                    <span className="font-semibold text-white min-w-[140px] sm:min-w-[180px]">
                      L&amp;D Initiatives:
                    </span>
                    <span className="text-gray-300">
                      May need to scale up to handle the increased number of new hires efficiently.
                    </span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2 mt-1">—</span>
                  <div className="flex flex-col sm:flex-row w-full">
                    <span className="font-semibold text-white min-w-[140px] sm:min-w-[180px]">
                      Operational Planning:
                    </span>
                    <span className="text-gray-300">
                      Ensuring that resources are allocated effectively to manage the ramp-up period for new employees without compromising on quality or productivity.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </ExpandableCallout>

     
        </PlaybookSection>
      </PlaybookSection>
    </div>
  );
};

export default StorytellingWithDataStage1; 